import type { Course } from '@mc/api'
import type { CoursesFixture, SubcatsFixture } from './types'

const urlSearchParam = (path: string, param: string) => {
  const urlObject = new URL(`https://masterclass.dev/${path}`)
  return urlObject.searchParams.get(param)
}

export const createClientUtils = ({
  coursesFixture,
  subcatsFixture,
}: {
  coursesFixture: CoursesFixture
  subcatsFixture: SubcatsFixture
}) => {
  const catSlugsToCourseSlugs = Object.fromEntries(
    Object.values(subcatsFixture).map((cat) => {
      const catCourseSlugs = cat.subcategories.flatMap(
        (subcat) => subcat.courses,
      )
      return [cat.slug, catCourseSlugs]
    }),
  )

  const getCatBySlug = (slug: string) =>
    subcatsFixture?.[slug as keyof typeof subcatsFixture]

  const getCategoryIds = (slug: string) => {
    const cats = Object.values(subcatsFixture)
    const campCats = cats.filter((cat) =>
      cat.subcategories.some((subcat) => subcat.courses.includes(slug)),
    )
    return campCats.map((cat) => cat.id)
  }

  const getPrimaryCategoryId = (slug: string) => {
    const courseOrCampMeta =
      coursesFixture?.[slug as keyof typeof coursesFixture]
    const primaryCatSlug = courseOrCampMeta?.primaryCategory
    const primaryCat = getCatBySlug(primaryCatSlug)
    return primaryCat?.id
  }

  const courseCategoryMatches = (course: Course, path: string) => {
    const filter = urlSearchParam(path, 'filter[category]')
    if (!filter) return true
    const catCourseSlugs = catSlugsToCourseSlugs[filter]
    return catCourseSlugs?.includes(course.slug) || false
  }

  const replaceCourseCategories = (course: Course, path: string) => {
    const filter = urlSearchParam(path, 'filter[category]')
    if (!filter) return course
    const newCourse =
      coursesFixture?.[course.slug as keyof typeof coursesFixture] || []
    const newCategories = (newCourse?.categories || []).map((slug) => ({
      id: getCatBySlug(slug).id,
    }))
    const newPrimaryCategory = {
      id: getCatBySlug(newCourse.primaryCategory).id,
    }
    return {
      ...course,
      categories: newCategories,
      primaryCategory: newPrimaryCategory,
    }
  }

  const getCategoriesFromFixture = () =>
    Object.values(subcatsFixture).map((cat) => ({
      id: cat.id,
      name: cat.name,
      slug: cat.slug,
      courses: [],
      camps: [],
    }))

  return {
    getCategoryIds,
    getPrimaryCategoryId,
    courseCategoryMatches,
    replaceCourseCategories,
    getCategoriesFromFixture,
  }
}
